// (SpeedIQReportActions defined below)

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Zap, GitBranch, Trophy, AlertTriangle, BookOpen, Share2, FileDown, Copy, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildRecommendations } from "@/lib/speediqRecommendations";

/**
 * SpeedIQResults — P5 · the post-analysis Results page.
 *
 * Renders the 3-layer scoring engine output (Mechanics + Performance + Cause
 * + Overall composite) plus the P6 corrective drill prescription stack.
 *
 * Embeds inline below the SpeedIQ capture wizard once /analyze returns.
 */
const LAYER_META = {
  mechanics:   { label: "Mechanics",   sub: "How clean is the movement?",      Icon: Activity,  color: "#3b82f6" },
  performance: { label: "Performance", sub: "How fast is the athlete?",        Icon: Zap,       color: "#f59e0b" },
  cause:       { label: "Cause",       sub: "Upstream blockers explained?",   Icon: GitBranch, color: "#a855f7" },
};

const BAND_STYLES = {
  elite:         { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)", text: "#34d399", label: "ELITE" },
  good:          { bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.4)",  text: "#86efac", label: "GOOD" },
  average:       { bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.4)", text: "#93c5fd", label: "AVG" },
  below_average: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", text: "#fbbf24", label: "BELOW" },
  poor:          { bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.45)", text: "#fca5a5", label: "POOR" },
  unrated:       { bg: "rgba(148,163,184,0.12)",border: "rgba(148,163,184,0.4)",text: "#cbd5e1", label: "UNRATED" },
};

export default function SpeedIQResults({ analyzeResponse }) {
  if (!analyzeResponse?.scores) return null;
  const sessionId = analyzeResponse?.session_id;
  const scores = analyzeResponse.scores;
  const overall = scores.overall || { score: null, band: "unrated" };
  const recs = buildRecommendations(analyzeResponse);
  const overallStyle = BAND_STYLES[overall.band] || BAND_STYLES.unrated;

  return (
    <div className="space-y-5" data-testid="speediq-results">
      {/* OVERALL HERO + share/PDF actions */}
      <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5 relative overflow-hidden" data-testid="speediq-overall-card">
        <div className="absolute top-0 right-0 bottom-0 w-1.5" style={{ backgroundColor: overallStyle.text }} />
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
              <Trophy className="h-3 w-3" /> SpeedIQ overall · 3-layer composite
            </div>
            <div className="font-display font-black text-5xl tracking-tighter mt-2" style={{ color: overallStyle.text }} data-testid="speediq-overall-score">
              {overall.score != null ? overall.score : "—"}
              <span className="text-base text-white/45 ml-2 font-bold">/ 100</span>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 inline-flex rounded border"
                 style={{ backgroundColor: overallStyle.bg, borderColor: overallStyle.border, color: overallStyle.text }}>
              {overallStyle.label}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 max-w-xs">
            <div className="text-[11px] text-white/50 text-right leading-relaxed">
              {overall.rated_layers ? (
                <>Composite of <b className="text-white">{overall.rated_layers.join(" · ")}</b>. Unrated layers' weights are redistributed.</>
              ) : (
                <>Not enough data to compose an overall score yet.</>
              )}
            </div>
            {sessionId && <SpeedIQReportActions sessionId={sessionId} />}
          </div>
        </div>
      </Card>

      {/* 3 LAYER CARDS */}
      <div className="grid md:grid-cols-3 gap-3" data-testid="speediq-layer-grid">
        {["mechanics", "performance", "cause"].map((k) => (
          <LayerCard key={k} layer={k} payload={scores[k]} />
        ))}
      </div>

      {/* CAUSE RULES NARRATIVE */}
      {scores.cause?.rules?.length > 0 && (
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="speediq-cause-rules">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-3" style={{ color: "var(--brand-secondary)" }}>
            Cross-module cause analysis · {scores.cause.rules.length} rule(s) fired
          </div>
          <ul className="space-y-4">
            {scores.cause.rules.map((r) => (
              <li key={r.rule} className="border-l-2 pl-3" style={{ borderColor: "var(--brand-secondary)" }} data-testid={`speediq-cause-rule-${r.rule}`}>
                <div className="font-bold text-white text-sm">{r.title}</div>
                <div className="text-[12px] text-white/75 leading-relaxed mt-1">{r.narrative}</div>
                <div className="text-[11px] text-white/60 mt-1.5">
                  <b style={{ color: "var(--brand-secondary)" }}>Next step:</b> {r.next_step}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[9px] uppercase tracking-wider text-white/40">
                  <span>{r.modules.join(" × ")}</span>
                  {r.citations && <span>· {r.citations.join(", ")}</span>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* CORRECTIVE STACK */}
      <RecommendationStack recs={recs} />
    </div>
  );
}


// ── Layer card ────────────────────────────────────────────────────────
function LayerCard({ layer, payload }) {
  const meta = LAYER_META[layer];
  const Icon = meta.Icon;
  const band = payload?.band || "unrated";
  const style = BAND_STYLES[band] || BAND_STYLES.unrated;
  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-4" data-testid={`speediq-layer-${layer}`}>
      <div className="flex items-center gap-2.5">
        <span className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white">{meta.label}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/45">{meta.sub}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold border"
              style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}>
          {style.label}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display font-black text-3xl font-mono-num text-white" data-testid={`speediq-layer-score-${layer}`}>
          {payload?.score != null ? payload.score : "—"}
        </span>
        <span className="text-xs text-white/40">/ 100</span>
      </div>
      <div className="mt-2 text-[11px] text-white/60 leading-snug">
        {layer === "mechanics" && payload && (
          <>{payload.side_flag_count} side flag(s) · {payload.rear_flag_count} rear flag(s){payload.capture_confidence_avg != null && <> · confidence {(payload.capture_confidence_avg * 100).toFixed(0)}%</>}</>
        )}
        {layer === "performance" && payload && (
          payload.score == null ? <span className="text-white/40">No splits submitted yet</span> :
          <>{Object.keys(payload.by_distance || {}).length} distance(s) benchmarked</>
        )}
        {layer === "cause" && payload && (
          <>{payload.rules_fired_count} cross-module rule(s) fired</>
        )}
      </div>
    </Card>
  );
}


// ── Report actions (P7 PDF + P8 share) ─────────────────────────────
function SpeedIQReportActions({ sessionId }) {
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/speediq/sessions/${sessionId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `speediq-${sessionId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Athlete report downloaded");
    } catch (e) { toast.error("Could not download PDF"); }
    finally { setBusy(null); }
  };
  const mintShare = async () => {
    setBusy("share");
    try {
      const { data } = await api.post(`/speediq/sessions/${sessionId}/share`);
      const fullUrl = `${window.location.origin}${data.share_path}`;
      await navigator.clipboard?.writeText(fullUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
      toast.success("Public share link copied");
    } catch (e) { toast.error("Could not mint share link"); }
    finally { setBusy(null); }
  };

  return (
    <div className="flex gap-2" data-testid="speediq-report-actions">
      <Button onClick={downloadPdf} disabled={busy === "pdf"} size="sm" variant="outline"
              className="border-white/15 text-white/85 bg-white/5 hover:bg-white/10 h-8 text-[11px]"
              data-testid="speediq-download-pdf">
        <FileDown className="h-3 w-3 mr-1.5" /> {busy === "pdf" ? "Building…" : "Athlete report PDF"}
      </Button>
      <Button onClick={mintShare} disabled={busy === "share"} size="sm"
              className="text-white red-glow h-8 text-[11px]"
              style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="speediq-share-link">
        {copied ? <Check className="h-3 w-3 mr-1.5" /> : <Share2 className="h-3 w-3 mr-1.5" />}
        {copied ? "Copied!" : busy === "share" ? "…" : "Share"}
      </Button>
    </div>
  );
}


// ── Recommendation stack (P6) ────────────────────────────────────────
function RecommendationStack({ recs }) {
  if (!recs?.length) {
    return (
      <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5 text-center text-white/50 text-sm" data-testid="speediq-recs-empty">
        No corrective prescriptions — all metrics within elite windows. Keep stacking quality reps.
      </Card>
    );
  }
  const byPhase = { I: [], II: [], III: [] };
  recs.forEach((r) => { if (byPhase[r.phase]) byPhase[r.phase].push(r); });
  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="speediq-recommendations">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
            <BookOpen className="h-3 w-3 inline mr-1" /> Recommended drill stack
          </div>
          <div className="font-display font-bold text-white text-lg mt-1">Phased corrective plan · {recs.length} prescription{recs.length === 1 ? "" : "s"}</div>
        </div>
        <div className="inline-flex items-center gap-2 text-[10px] text-white/45 uppercase tracking-wider">
          <AlertTriangle className="h-3 w-3" /> Coach overrides take priority
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {["I", "II", "III"].map((phase) => (
          <div key={phase} className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`speediq-rec-phase-${phase}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/55 mb-2">
              Phase {phase} · {phase === "I" ? "Tissue & mobility" : phase === "II" ? "Capacity build" : "Movement re-pattern"}
            </div>
            <ul className="space-y-3">
              {byPhase[phase].length === 0 && <li className="text-[11px] text-white/35 italic">—</li>}
              {byPhase[phase].map((r, i) => (
                <li key={`${r.metric || r.rule}-${i}`} className="text-[12px]" data-testid={`speediq-rec-${phase}-${i}`}>
                  <div className="font-bold text-white">{r.title}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(r.drills || []).map((d) => (
                      <span key={d} className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-white/5 border border-white/10 text-white/75">{d}</span>
                    ))}
                  </div>
                  <div className="text-[10px] text-white/45 mt-1">
                    {r.duration_weeks} wk{r.duration_weeks === 1 ? "" : "s"}{r.citation && <> · {r.citation}</>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
